import { Assert } from '@cotera/utilities';
import { err, ok } from 'neverthrow';
import { Graph } from '../graph';
import { Commit } from './commit';
import { IdentityMap } from './identity-map';
import { CommitsBuildFailure, CommitsBuildResult } from './types';
import { BuildExplorer } from './build-explorer';
import { DocumentEditor } from '../document';

/**
 * Extract document references from a TiptapDocument source.
 * Uses EditorState.getDependencies() to find all dependencies including:
 * - Mentions (from mention nodes, excluding tool and column mentions)
 * - Symbol references in expression nodes
 * - Symbol references in attachment nodes (when not freehand)
 * Note: Uses empty graph since we don't need recursive extraction here.
 * Note: Tool mentions are excluded because tools are resolved via toolResolver.
 * Note: Column mentions are excluded because they reference columns within the same relation.
 */
const extractDocumentRefs = (
  source: DocumentEditor.DocumentState
): string[] => {
  const editor = new DocumentEditor.EditorState(source);
  // getDependencies() returns symbol references (excluding tools and columns which are resolved differently)
  return editor.getDependencies(BuildExplorer.empty());
};

/**
 * Extract only document mentions (for circular reference checking between symbols).
 * These are the types that can create circular dependencies.
 * Note: Uses empty graph since we don't need recursive extraction here.
 */
const extractCircularDeps = (
  source: DocumentEditor.DocumentState
): string[] => {
  const editor = new DocumentEditor.EditorState(source);
  return editor
    .getMentions(BuildExplorer.empty())
    .filter((x) => x.type === 'document')
    .map((mention) => mention.id);
};

/**
 * Extract column mentions from an LLM v2 column's editor state.
 * Column mentions create dependencies between columns in the same relation.
 * Note: Uses empty graph since we don't need recursive extraction here.
 */
const extractColumnMentions = (
  source: DocumentEditor.DocumentState
): string[] => {
  const editor = new DocumentEditor.EditorState(source);
  return editor
    .getMentions(BuildExplorer.empty())
    .filter((x) => x.type === 'column')
    .map((mention) => mention.id);
};

/**
 * Update the h1 title in a DocumentState to a new value.
 * If the document already has an h1 as the first element, it replaces it.
 * Otherwise, it prepends a new h1.
 */
const updateDocumentTitle = (
  state: DocumentEditor.DocumentState,
  newTitle: string
): DocumentEditor.DocumentState => {
  const hasHeadingAsFirst =
    state.content?.[0]?.type === 'heading' &&
    state.content?.[0].attrs?.['level'] === 1;

  return {
    ...state,
    content: [
      {
        type: 'heading',
        attrs: { level: 1 },
        content: [{ type: 'text', text: newTitle }],
      },
      ...(hasHeadingAsFirst
        ? state.content?.slice(1) ?? []
        : state.content ?? []),
    ],
  };
};

/**
 * Check if adding a column dependency would create a circular reference.
 * Uses depth-first search to detect cycles in the column dependency graph.
 *
 * The dependencies map format: columnDeps[X] = [Y, Z] means Y and Z depend on column X.
 * To check if adding "from → to" creates a cycle, we need to see if there's already
 * a path "to → ... → from". If there is, then adding "from → to" would create a cycle.
 */
const wouldCreateColumnCircularReference = (
  from: string,
  to: string,
  columnDeps: Record<string, string[]>
): boolean => {
  const visited = new Set<string>();
  const stack = [to];

  while (stack.length > 0) {
    const current = stack.pop()!;

    if (current === from) {
      return true; // Found a cycle
    }

    if (visited.has(current)) {
      continue;
    }

    visited.add(current);

    // Find what current depends on by scanning for keys that have current in their values
    // If columnDeps[X] contains current, that means current depends on X (current → X)
    for (const [target, dependents] of Object.entries(columnDeps)) {
      if (dependents.includes(current)) {
        stack.push(target);
      }
    }
  }

  return false;
};

/**
 * Check if adding a dependency would create a circular reference.
 * Uses depth-first search to detect cycles in the dependency graph.
 *
 * The dependencies map format: dependencies[X] = [Y, Z] means Y and Z depend on X.
 * To check if adding "from → to" creates a cycle, we need to see if there's already
 * a path "to → ... → from". If there is, then adding "from → to" would create a cycle.
 *
 * To traverse forward (following "depends on" edges), starting from 'to':
 * - We need to find what 'to' depends on
 * - If 'to' depends on X, then dependencies[X] includes 'to'
 * - So we need to scan all keys K where dependencies[K] contains 'to'
 */
const wouldCreateCircularReference = (
  from: string,
  to: string,
  dependencies: Record<string, string[]>
): boolean => {
  const visited = new Set<string>();
  const stack = [to];

  while (stack.length > 0) {
    const current = stack.pop()!;

    if (current === from) {
      return true; // Found a cycle
    }

    if (visited.has(current)) {
      continue;
    }

    visited.add(current);

    // Find what current depends on by scanning for keys that have current in their values
    // If dependencies[X] contains current, that means current depends on X (current → X)
    for (const [target, dependents] of Object.entries(dependencies)) {
      if (dependents.includes(current)) {
        stack.push(target);
      }
    }
  }

  return false;
};

export const buildCommits = (
  commits: readonly Commit[],
  opts: {
    readonly toolResolver: Graph.IToolResolver;
  }
): CommitsBuildResult => {
  const { toolResolver } = opts;
  let symbols: Record<string, Graph.Identity> = {};
  // Track dependencies: key is the symbol being depended on, value is array of symbols that depend on it
  let dependencies: Record<string, string[]> = {};
  // Track column dependencies per relation: columnDeps[relName][colName] = [dependentCols]
  // Format: columnDeps[X] = [Y, Z] means columns Y and Z depend on column X
  let columnDeps: Record<string, Record<string, string[]>> = {};

  let identities = IdentityMap.new();

  for (const [cIdx, commit] of commits.entries()) {
    opLoop: for (const [opIdx, op] of commit.operations.entries()) {
      const at = Graph.Identity.forOperation(cIdx, opIdx);

      switch (op.t) {
        case 'create-expr': {
          const sym = op.symbol;

          if (Object.hasOwn(symbols, sym)) {
            return err({ err: { t: 'symbol-taken', sym }, at });
          }

          const node: Graph.GraphNode = {
            t: 'expr',
            source: op.source,
            meta: {
              identity: at,
              editsAt: [at],
              isLocked: false,
            },
            ...(op.metadata && { metadata: op.metadata }),
          };

          identities = identities.add(at, node);
          symbols = { ...symbols, [sym]: at };

          continue opLoop;
        }
        case 'create-document': {
          const sym = op.symbol;

          if (Object.hasOwn(symbols, sym)) {
            return err({ err: { t: 'symbol-taken', sym }, at });
          }

          const node: Graph.GraphNode = {
            t: 'document',
            source: op.source,
            meta: {
              identity: at,
              editsAt: [at],
              isLocked: false,
            },
            ...(op.metadata && { metadata: op.metadata }),
          };

          // First validate all references exist
          const refs = extractDocumentRefs(op.source);
          for (const refSym of refs) {
            if (!symbols[refSym]) {
              return err({
                err: { t: 'symbol-not-mapped', sym: refSym },
                at,
              });
            }
          }

          // Check for circular references BEFORE adding to dependencies
          const circularDeps = extractCircularDeps(op.source);
          for (const refSym of circularDeps) {
            // Check for self-reference
            if (refSym === sym) {
              return err({
                err: { t: 'circular-reference', from: sym, to: refSym },
                at,
              });
            }

            // Check if adding this dependency would create a circular reference
            if (wouldCreateCircularReference(sym, refSym, dependencies)) {
              return err({
                err: { t: 'circular-reference', from: sym, to: refSym },
                at,
              });
            }
          }

          // Now add dependencies after validation
          for (const refSym of refs) {
            dependencies = {
              ...dependencies,
              [refSym]: [...(dependencies[refSym] || []), sym],
            };
          }

          identities = identities.add(at, node);
          symbols = { ...symbols, [sym]: at };

          continue opLoop;
        }
        case 'create-rel': {
          const sym = op.symbol;

          if (Object.hasOwn(symbols, sym)) {
            return err({ err: { t: 'symbol-taken', sym }, at: at });
          }

          const node: Graph.GraphNode = {
            t: 'rel',
            source: op.source,
            where: op.where ?? null,
            cols: {},
            pk: op.pk ?? null,
            meta: {
              identity: at,
              editsAt: [at],
              isLocked: false,
            },
          };

          identities = identities.add(at, node);
          symbols = { ...symbols, [sym]: at };

          continue opLoop;
        }
        case 'create-column': {
          const relIdentity = symbols[op.relName];

          if (relIdentity === undefined) {
            return err({
              err: { t: 'symbol-not-mapped', sym: op.relName },
              at: at,
            });
          }

          // Check for column circular dependencies if this is an LLM v2 column
          if (
            op.config.t === 'llm' &&
            op.config.version === 'v2' &&
            op.config.editorState !== undefined
          ) {
            const colMentions = extractColumnMentions(op.config.editorState);
            const relColDeps = columnDeps[op.relName] ?? {};

            for (const mentionedCol of colMentions) {
              // Check for self-reference
              if (mentionedCol === op.name) {
                return err({
                  err: {
                    t: 'circular-reference',
                    from: `${op.relName}.${op.name}`,
                    to: `${op.relName}.${mentionedCol}`,
                  },
                  at,
                });
              }

              // Check if adding this dependency would create a circular reference
              if (
                wouldCreateColumnCircularReference(
                  op.name,
                  mentionedCol,
                  relColDeps
                )
              ) {
                return err({
                  err: {
                    t: 'circular-reference',
                    from: `${op.relName}.${op.name}`,
                    to: `${op.relName}.${mentionedCol}`,
                  },
                  at,
                });
              }
            }

            // Add column dependencies after validation
            for (const mentionedCol of colMentions) {
              columnDeps = {
                ...columnDeps,
                [op.relName]: {
                  ...(columnDeps[op.relName] ?? {}),
                  [mentionedCol]: [
                    ...((columnDeps[op.relName] ?? {})[mentionedCol] ?? []),
                    op.name,
                  ],
                },
              };
            }
          }

          const identRes = identities.editRel(relIdentity, at, (node) => {
            if (node.t !== 'rel') {
              return err({ t: 'can-only-add-col-to-rel', got: node.t });
            }

            const existingCol = node.cols[op.name];

            if (existingCol) {
              return err({
                t: 'col-already-exists',
                colName: op.name,
              });
            }

            const colNode: Graph.ColNode = {
              meta: {
                isLocked: false,
                editsAt: [at],
                identity: at,
              },
              def: op.config,
            };

            return ok({
              ...node,
              cols: {
                ...node.cols,
                [op.name]: colNode,
              },
            });
          });

          if (identRes.isErr()) {
            return err({ err: identRes.error, at });
          }

          identities = identRes.value;

          continue opLoop;
        }
        case 'create-agent': {
          const sym = op.symbol;

          if (Object.hasOwn(symbols, sym)) {
            return err({ err: { t: 'symbol-taken', sym }, at });
          }

          const node: Graph.GraphNode = {
            t: 'agent',
            outputTy: op.outputTy,
            editorState: op.editorState,
            model: op.model,
            schedule: op.schedule,
            meta: {
              identity: at,
              editsAt: [at],
              isLocked: false,
            },
          };

          // First validate all references exist
          const refs = extractDocumentRefs(
            op.editorState as DocumentEditor.DocumentState
          );
          // for (const refSym of refs) {
          //   if (!symbols[refSym]) {
          //     return err({
          //       err: { t: 'symbol-not-mapped', sym: refSym },
          //       at,
          //     });
          //   }
          // }

          // Check for circular references BEFORE adding to dependencies
          const circularDeps = extractCircularDeps(
            op.editorState as DocumentEditor.DocumentState
          );
          for (const refSym of circularDeps) {
            // Check for self-reference
            if (refSym === sym) {
              return err({
                err: { t: 'circular-reference', from: sym, to: refSym },
                at,
              });
            }

            // Check if adding this dependency would create a circular reference
            if (wouldCreateCircularReference(sym, refSym, dependencies)) {
              return err({
                err: { t: 'circular-reference', from: sym, to: refSym },
                at,
              });
            }
          }

          // Now add dependencies after validation
          for (const refSym of refs) {
            dependencies = {
              ...dependencies,
              [refSym]: [...(dependencies[refSym] || []), sym],
            };
          }

          identities = identities.add(at, node);
          symbols = { ...symbols, [sym]: at };

          continue opLoop;
        }
        case 'create-tool': {
          const sym = op.symbol;

          if (Object.hasOwn(symbols, sym)) {
            return err({ err: { t: 'symbol-taken', sym }, at });
          }

          const node: Graph.GraphNode = {
            t: 'tool',
            def: op.def,
            meta: {
              identity: at,
              editsAt: [at],
              isLocked: false,
            },
          };

          identities = identities.add(at, node);
          symbols = { ...symbols, [sym]: at };

          continue opLoop;
        }
        case 'update-agent': {
          const sym = op.symbol;
          const existing = symbols[sym];

          if (!existing) {
            return err({ err: { t: 'symbol-not-mapped', sym }, at });
          }

          // Update dependencies if updating editor state
          if (op.editorState !== undefined) {
            // First validate all references exist
            const refs = extractDocumentRefs(
              op.editorState as DocumentEditor.DocumentState
            );
            // for (const refSym of refs) {
            //   if (!symbols[refSym]) {
            //     return err({
            //       err: { t: 'symbol-not-mapped', sym: refSym },
            //       at,
            //     });
            //   }
            // }

            // Create temporary dependencies map without this symbol's old dependencies
            let tempDependencies = { ...dependencies };
            for (const [dep, dependents] of Object.entries(tempDependencies)) {
              tempDependencies = {
                ...tempDependencies,
                [dep]: (dependents ?? []).filter((d) => d !== sym),
              };
            }

            // Check for circular references
            const circularDeps = extractCircularDeps(
              op.editorState as DocumentEditor.DocumentState
            );
            for (const refSym of circularDeps) {
              // Check for self-reference
              if (refSym === sym) {
                return err({
                  err: { t: 'circular-reference', from: sym, to: refSym },
                  at,
                });
              }

              // Check if adding this dependency would create a circular reference
              // We check WITHOUT adding the new dependency to see if refSym already has a path back to sym
              if (wouldCreateCircularReference(sym, refSym, tempDependencies)) {
                return err({
                  err: { t: 'circular-reference', from: sym, to: refSym },
                  at,
                });
              }
            }

            // Now update dependencies after validation
            // Remove old dependencies for this symbol
            for (const [dep, dependents] of Object.entries(dependencies)) {
              dependencies = {
                ...dependencies,
                [dep]: (dependents ?? []).filter((d) => d !== sym),
              };
            }

            // Add new dependencies
            for (const refSym of refs) {
              dependencies = {
                ...dependencies,
                [refSym]: [...(dependencies[refSym] || []), sym],
              };
            }
          }

          //these opts are spead to avoid overriding a set value to undefined
          const editRes = identities.editAgent(existing, at, (node) => {
            return ok({
              ...node,
              ...(op.outputTy !== undefined && { outputTy: op.outputTy }),
              ...(op.editorState !== undefined && {
                editorState: op.editorState,
              }),
              ...(op.model !== undefined && { model: op.model }),
              ...(op.schedule !== undefined ? { schedule: op.schedule } : {}),
            });
          });

          if (editRes.isErr()) {
            return err({ err: editRes.error, at });
          }

          identities = editRes.value;
          continue opLoop;
        }
        case 'update-tool': {
          const sym = op.symbol;
          const existing = symbols[sym];

          if (!existing) {
            return err({ err: { t: 'symbol-not-mapped', sym }, at });
          }

          const editRes = identities.editTool(existing, at, (node) => {
            return ok({
              ...node,
              ...(op.def !== undefined && { def: op.def }),
            });
          });

          if (editRes.isErr()) {
            return err({ err: editRes.error, at });
          }

          identities = editRes.value;
          continue opLoop;
        }
        case 'update-expr': {
          const sym = op.symbol;
          const existing = symbols[sym];

          if (!existing) {
            return err({ err: { t: 'symbol-not-mapped', sym }, at: at });
          }

          const editRes = identities.editExpr(existing, at, (node) => {
            return ok({
              ...node,
              source: op.source ?? node.source,
              ...(op.metadata !== undefined && { metadata: op.metadata }),
            });
          });

          if (editRes.isErr()) {
            return err({ err: editRes.error, at: at });
          }

          identities = editRes.value;
          continue opLoop;
        }
        case 'update-document': {
          const sym = op.symbol;
          const existing = symbols[sym];

          if (!existing) {
            return err({ err: { t: 'symbol-not-mapped', sym }, at: at });
          }

          // Get the node to access current source
          const currentNode = identities.get(existing);
          if (!currentNode || currentNode.t !== 'document') {
            return err({
              err: { t: 'symbol-not-mapped', sym },
              at: at,
            });
          }

          const newSource = op.source ?? currentNode.source;

          // First validate all references exist
          const refs = extractDocumentRefs(newSource);
          for (const refSym of refs) {
            if (!symbols[refSym]) {
              return err({
                err: { t: 'symbol-not-mapped', sym: refSym },
                at,
              });
            }
          }

          // Create temporary dependencies map without this symbol's old dependencies
          let tempDependencies = { ...dependencies };
          for (const [dep, dependents] of Object.entries(tempDependencies)) {
            tempDependencies = {
              ...tempDependencies,
              [dep]: (dependents ?? []).filter((d) => d !== sym),
            };
          }

          // Check for circular references
          const circularDeps = extractCircularDeps(newSource);
          for (const refSym of circularDeps) {
            // Check for self-reference
            if (refSym === sym) {
              return err({
                err: { t: 'circular-reference', from: sym, to: refSym },
                at,
              });
            }

            // Check if adding this dependency would create a circular reference
            // We check WITHOUT adding the new dependency to see if refSym already has a path back to sym
            if (wouldCreateCircularReference(sym, refSym, tempDependencies)) {
              return err({
                err: { t: 'circular-reference', from: sym, to: refSym },
                at,
              });
            }
          }

          // Now update dependencies after validation
          // Remove old dependencies for this symbol
          for (const [dep, dependents] of Object.entries(dependencies)) {
            dependencies = {
              ...dependencies,
              [dep]: (dependents ?? []).filter((d) => d !== sym),
            };
          }

          // Add new dependencies
          for (const refSym of refs) {
            dependencies = {
              ...dependencies,
              [refSym]: [...(dependencies[refSym] || []), sym],
            };
          }

          const editRes = identities.editDocument(existing, at, (node) => {
            return ok({
              ...node,
              source: newSource,
              ...(op.metadata !== undefined && { metadata: op.metadata }),
            });
          });

          if (editRes.isErr()) {
            return err({ err: editRes.error, at: at });
          }

          identities = editRes.value;
          continue opLoop;
        }
        case 'update-rel': {
          const sym = op.symbol;
          const existing = symbols[sym];

          if (!existing) {
            return err({ err: { t: 'symbol-not-mapped', sym }, at: at });
          }

          const editRes = identities.editRel(existing, at, (node) => {
            if (node.t !== 'rel') {
              return err({
                t: 'cant-update-different-kind',
                curr: node.t,
                attempted: 'rel',
              });
            }

            // TODO: check whether primary key expression is actually
            // valid errorql

            return ok({
              ...node,
              source: op.source ?? node.source,
              where: op.where === undefined ? node.where : op.where,
              pk: op.pk === undefined ? node.pk : op.pk,
            });
          });

          if (editRes.isErr()) {
            return err({ err: editRes.error, at: at });
          }

          identities = editRes.value;
          continue opLoop;
        }
        case 'rename': {
          const { [op.symbol]: existing, ...rest } = symbols;

          if (!existing) {
            return err({
              err: { t: 'symbol-not-mapped', sym: op.symbol },
              at: at,
            });
          }

          // Update dependency tracking: move all dependents to new symbol
          const { [op.symbol]: oldDeps, ...restDeps } = dependencies;
          dependencies = {
            ...restDeps,
            [op.newSymbol]: oldDeps || [],
          };

          identities = identities.addEdit(existing, at);
          symbols = { ...rest, [op.newSymbol]: existing };
          continue opLoop;
        }
        case 'delete': {
          const { [op.symbol]: existing, ...rest } = symbols;

          if (!existing) {
            return err({
              err: { t: 'symbol-not-mapped', sym: op.symbol },
              at: at,
            });
          }

          identities = identities.addEdit(existing, at);
          symbols = rest;
          // Clean up any dependencies tracked for this symbol
          const { [op.symbol]: _, ...restDeps } = dependencies;
          dependencies = restDeps;
          continue opLoop;
        }
        case 'delete-col': {
          const rel = symbols[op.relName];

          if (!rel) {
            return err({
              err: { t: 'symbol-not-mapped', sym: op.relName },
              at: at,
            });
          }

          const identRes = identities.editRel(rel, at, (node) => {
            const { cols, ...rest } = node;
            const { [op.name]: existing, ...restCols } = cols;

            // TODO: allow column idents that would give put a place for this
            // to be deleted
            if (!existing) {
              return err({ t: 'col-not-found', colName: op.name });
            }

            return ok({ ...rest, cols: restCols });
          });

          if (identRes.isErr()) {
            return err({ err: identRes.error, at });
          }

          identities = identRes.value;

          // Clean up column dependencies for this column
          if (columnDeps[op.relName]) {
            const { [op.name]: _, ...restColDeps } = columnDeps[op.relName]!;
            // Also remove this column from being a dependent of other columns
            const cleanedDeps: Record<string, string[]> = {};
            for (const [col, dependents] of Object.entries(restColDeps)) {
              cleanedDeps[col] = (dependents ?? []).filter(
                (d) => d !== op.name
              );
            }
            columnDeps = {
              ...columnDeps,
              [op.relName]: cleanedDeps,
            };
          }

          continue opLoop;
        }
        case 'replace-column':
        case 'update-column': {
          const { relName, name, config } = op;
          const rel = symbols[relName];

          if (!rel) {
            return err({
              err: { t: 'symbol-not-mapped', sym: relName },
              at: at,
            });
          }

          // Check for column circular dependencies if this is an LLM v2 column
          if (
            config.t === 'llm' &&
            config.version === 'v2' &&
            config.editorState !== undefined
          ) {
            const colMentions = extractColumnMentions(config.editorState);

            // Create temporary column deps without this column's old dependencies
            let tempColDeps = { ...(columnDeps[relName] ?? {}) };
            for (const [dep, dependents] of Object.entries(tempColDeps)) {
              tempColDeps = {
                ...tempColDeps,
                [dep]: (dependents ?? []).filter((d) => d !== name),
              };
            }

            for (const mentionedCol of colMentions) {
              // Check for self-reference
              if (mentionedCol === name) {
                return err({
                  err: {
                    t: 'circular-reference',
                    from: `${relName}.${name}`,
                    to: `${relName}.${mentionedCol}`,
                  },
                  at,
                });
              }

              // Check if adding this dependency would create a circular reference
              if (
                wouldCreateColumnCircularReference(
                  name,
                  mentionedCol,
                  tempColDeps
                )
              ) {
                return err({
                  err: {
                    t: 'circular-reference',
                    from: `${relName}.${name}`,
                    to: `${relName}.${mentionedCol}`,
                  },
                  at,
                });
              }
            }

            // Update column dependencies after validation
            // First remove old dependencies for this column
            let relColDeps = columnDeps[relName] ?? {};
            for (const [dep, dependents] of Object.entries(relColDeps)) {
              relColDeps = {
                ...relColDeps,
                [dep]: (dependents ?? []).filter((d) => d !== name),
              };
            }

            // Then add new dependencies
            for (const mentionedCol of colMentions) {
              relColDeps = {
                ...relColDeps,
                [mentionedCol]: [...(relColDeps[mentionedCol] ?? []), name],
              };
            }

            columnDeps = {
              ...columnDeps,
              [relName]: relColDeps,
            };
          }

          const identityRes = identities.editRel(rel, at, (node) => {
            const { [name]: col, ...rest } = node.cols;

            if (!col) {
              const e: CommitsBuildFailure = {
                t: 'col-not-found',
                colName: name,
              };
              return err(e);
            }

            const newMeta: Graph.NodeMeta =
              op.t === 'update-column'
                ? {
                    ...col.meta,
                    editsAt: [...col.meta.editsAt, at],
                  }
                : {
                    isLocked: false,
                    identity: at,
                    editsAt: [...col.meta.editsAt, at],
                    predecessor: col.meta.identity,
                  };

            const newCol: Graph.ColNode = {
              def: config,
              meta: newMeta,
            };

            const newNode: Omit<Graph.RelNode, 'meta'> = {
              ...node,
              cols: { ...rest, [name]: newCol },
            };

            return ok(newNode);
          });

          if (identityRes.isErr()) {
            return err({ err: identityRes.error, at });
          }

          identities = identityRes.value;
          continue opLoop;
        }
        case 'rename-col': {
          const { relName, name, newName } = op;
          const rel = symbols[relName];

          if (!rel) {
            return err({
              err: { t: 'symbol-not-mapped', sym: relName },
              at: at,
            });
          }

          const identityRes = identities.editRel(rel, at, (node) => {
            if (Object.hasOwn(node.cols, newName)) {
              const e: CommitsBuildFailure = {
                t: 'col-already-exists',
                colName: newName,
              };
              return err(e);
            }

            const { [name]: col, ...rest } = node.cols;

            if (!col) {
              const e: CommitsBuildFailure = {
                t: 'col-not-found',
                colName: name,
              };
              return err(e);
            }

            const newNode: Omit<Graph.RelNode, 'meta'> = {
              ...node,
              cols: { ...rest, [newName]: col },
            };

            return ok(newNode);
          });

          if (identityRes.isErr()) {
            return err({ err: identityRes.error, at });
          }

          identities = identityRes.value;

          // Update column dependency tracking: move all dependents to new column name
          if (columnDeps[relName]) {
            const relColDeps = columnDeps[relName]!;
            const { [name]: oldDeps, ...restColDeps } = relColDeps;
            // Move dependents to new name
            const updatedDeps: Record<string, string[]> = {
              ...restColDeps,
              [newName]: oldDeps ?? [],
            };
            // Also update any columns that depended on the old name to depend on the new name
            for (const [col, dependents] of Object.entries(updatedDeps)) {
              updatedDeps[col] = (dependents ?? []).map((d) =>
                d === name ? newName : d
              );
            }
            columnDeps = {
              ...columnDeps,
              [relName]: updatedDeps,
            };
          }

          continue opLoop;
        }
        case 'lock':
        case 'unlock': {
          const sym = op.symbol;
          const existing = symbols[sym];

          if (!existing) {
            return err({ err: { t: 'symbol-not-mapped', sym }, at: at });
          }

          identities = identities.setLockStatus(existing, at, op.t === 'lock');

          continue opLoop;
        }
        case 'duplicate': {
          // Handle column duplication
          if (op.relName !== undefined && op.name !== undefined) {
            const sourceRelName = op.relName;
            const colName = op.name;
            const newColName = op.newName ?? colName;
            // newSymbol can specify the target relation (for cross-relation duplication)
            // If not provided, duplicate within the same relation
            const targetRelName = op.newSymbol ?? sourceRelName;

            const sourceRelIdentity = symbols[sourceRelName];
            if (!sourceRelIdentity) {
              return err({
                err: { t: 'symbol-not-mapped', sym: sourceRelName },
                at,
              });
            }

            const sourceRelNode = identities.get(sourceRelIdentity);
            if (!sourceRelNode || sourceRelNode.t !== 'rel') {
              return err({
                err: { t: 'symbol-not-mapped', sym: sourceRelName },
                at,
              });
            }

            const sourceCol = sourceRelNode.cols[colName];
            if (!sourceCol) {
              return err({
                err: {
                  t: 'symbol-not-mapped',
                  sym: `${sourceRelName}.${colName}`,
                },
                at,
              });
            }

            // Get target relation
            const targetRelIdentity = symbols[targetRelName];
            if (!targetRelIdentity) {
              return err({
                err: { t: 'symbol-not-mapped', sym: targetRelName },
                at,
              });
            }

            // Add column to target relation
            const identRes = identities.editRel(
              targetRelIdentity,
              at,
              (node) => {
                if (node.t !== 'rel') {
                  return err({ t: 'can-only-add-col-to-rel', got: node.t });
                }

                // Check if new column name already exists
                if (node.cols[newColName]) {
                  return err({
                    t: 'col-already-exists',
                    colName: newColName,
                  });
                }

                // Create new column with same config but new identity and predecessor
                // For LLM v2 columns, also update the h1 title in editorState
                let newDef = sourceCol.def;
                if (
                  sourceCol.def.t === 'llm' &&
                  'version' in sourceCol.def &&
                  sourceCol.def.version === 'v2'
                ) {
                  newDef = {
                    ...sourceCol.def,
                    editorState: updateDocumentTitle(
                      sourceCol.def.editorState as DocumentEditor.DocumentState,
                      newColName
                    ),
                  };
                }

                const newColNode: Graph.ColNode = {
                  ...sourceCol,
                  def: newDef,
                  meta: {
                    identity: at,
                    editsAt: [at],
                    isLocked: false,
                    predecessor: sourceCol.meta.identity,
                  },
                };

                return ok({
                  ...node,
                  cols: {
                    ...node.cols,
                    [newColName]: newColNode,
                  },
                });
              }
            );

            if (identRes.isErr()) {
              return err({ err: identRes.error, at });
            }

            identities = identRes.value;

            continue opLoop;
          }

          // Handle top-level symbol duplication
          const sourceSym = op.symbol;
          const newSym = op.newSymbol;

          if (!sourceSym) {
            return err({
              err: { t: 'symbol-not-mapped', sym: 'symbol is required' },
              at,
            });
          }

          const sourceIdentity = symbols[sourceSym];

          if (!sourceIdentity) {
            return err({
              err: { t: 'symbol-not-mapped', sym: sourceSym },
              at: at,
            });
          }

          if (Object.hasOwn(symbols, newSym)) {
            return err({ err: { t: 'symbol-taken', sym: newSym }, at });
          }

          const sourceNode = identities.get(sourceIdentity);

          if (!sourceNode) {
            return err({
              err: { t: 'symbol-not-mapped', sym: sourceSym },
              at: at,
            });
          }

          // Create new node with same config but new identity and predecessor
          const newNode: Graph.GraphNode = (() => {
            const { meta: _meta, ...nodeData } = sourceNode;
            const newMeta: Graph.NodeMeta = {
              identity: at,
              editsAt: [at],
              isLocked: false,
              predecessor: sourceIdentity,
            };

            switch (sourceNode.t) {
              case 'expr': {
                return {
                  ...nodeData,
                  meta: newMeta,
                } as Graph.ExprNode;
              }
              case 'document': {
                return {
                  ...nodeData,
                  meta: newMeta,
                } as Graph.DocumentNode;
              }
              case 'rel': {
                // For rel nodes, create the relation with empty columns
                // Columns will be duplicated via separate operations to ensure
                // each column gets a unique identity based on its own operation
                const relNode = nodeData as Omit<Graph.RelNode, 'meta'>;
                return {
                  ...relNode,
                  cols: {}, // Start with empty columns, they'll be added via separate operations
                  meta: newMeta,
                } as Graph.RelNode;
              }
              case 'agent': {
                const agentNode = nodeData as Omit<Graph.AgentNode, 'meta'>;
                return {
                  ...agentNode,
                  editorState: updateDocumentTitle(
                    agentNode.editorState as DocumentEditor.DocumentState,
                    newSym
                  ),
                  meta: newMeta,
                } as Graph.AgentNode;
              }
              case 'tool': {
                return {
                  ...nodeData,
                  meta: newMeta,
                } as Graph.ToolNode;
              }
              default:
                return Assert.unreachable(sourceNode);
            }
          })();

          // Handle dependencies for document and agent nodes
          if (sourceNode.t === 'document' || sourceNode.t === 'agent') {
            const source =
              sourceNode.t === 'document'
                ? sourceNode.source
                : (sourceNode.editorState as DocumentEditor.DocumentState);

            // Validate all references exist
            const refs = extractDocumentRefs(source);
            for (const refSym of refs) {
              if (!symbols[refSym]) {
                return err({
                  err: { t: 'symbol-not-mapped', sym: refSym },
                  at,
                });
              }
            }

            // Check for circular references
            const circularDeps = extractCircularDeps(source);
            for (const refSym of circularDeps) {
              // Check for self-reference
              if (refSym === newSym) {
                return err({
                  err: { t: 'circular-reference', from: newSym, to: refSym },
                  at,
                });
              }

              // Check if adding this dependency would create a circular reference
              if (wouldCreateCircularReference(newSym, refSym, dependencies)) {
                return err({
                  err: { t: 'circular-reference', from: newSym, to: refSym },
                  at,
                });
              }
            }

            // Add dependencies
            for (const refSym of refs) {
              dependencies = {
                ...dependencies,
                [refSym]: [...(dependencies[refSym] || []), newSym],
              };
            }
          }

          identities = identities.add(at, newNode);
          symbols = { ...symbols, [newSym]: at };

          continue opLoop;
        }
        default:
          return Assert.unreachable(op);
      }
    }
  }

  return ok(
    new BuildExplorer(
      symbols,
      identities,
      commits,
      undefined,
      toolResolver,
      undefined
    )
  );
};
